import { Component, Input, Output, EventEmitter, computed, signal } from '@angular/core';

import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { IconGlassComponent } from '../icon/icon-glass.component';
import { AccordionItem, AccordionVariant, AccordionSize } from '../models/accordion.model';

export { AccordionItem, AccordionVariant, AccordionSize } from '../models/accordion.model';

const accordionVariants = cva({
  base: [
    'relative backdrop-blur-xl border rounded-lg transition-all duration-300 ease-out',
    'overflow-hidden',
    // Glass morphism base
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-60 before:rounded-lg before:pointer-events-none',
    'before:transition-opacity before:duration-300',
  ].join(' '),
  variants: {
    variant: {
      default: [
        // Light theme
        'bg-white/15 border-white/30',
        'shadow-lg shadow-black/5',
        // Dark theme
        'dark:bg-white/10 dark:border-white/20',
        'dark:shadow-lg dark:shadow-black/20',
      ].join(' '),
      elevated: [
        // Light theme
        'bg-white/20 border-primary-200/40',
        'shadow-xl shadow-primary-500/10',
        // Dark theme
        'dark:bg-white/15 dark:border-primary-400/30',
        'dark:shadow-xl dark:shadow-primary-500/20',
      ].join(' '),
      premium: [
        // Ultra-premium for e-commerce
        'bg-linear-to-br from-white/25 via-white/15 to-primary-50/25',
        'border-2 border-primary-300/50',
        'shadow-xl shadow-primary-500/15',
        // Dark theme
        'dark:bg-linear-to-br dark:from-white/20 dark:via-white/10 dark:to-primary-900/25',
        'dark:border-2 dark:border-primary-400/40',
        'dark:shadow-xl dark:shadow-primary-500/25',
      ].join(' '),
      minimal: [
        'bg-white/10 border-gray-200/25',
        'shadow-md shadow-gray-500/5',
        'dark:bg-white/8 dark:border-gray-600/20',
        'dark:shadow-md dark:shadow-black/15',
      ].join(' '),
      bordered: [
        'bg-white/12 border-2 border-white/40',
        'shadow-lg shadow-black/8',
        'dark:bg-white/8 dark:border-white/25',
        'dark:shadow-lg dark:shadow-black/25',
      ].join(' '),
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const accordionItemVariants = cva({
  base: [
    'relative border-b border-white/20 dark:border-white/15 last:border-b-0',
    'transition-all duration-300 ease-out',
  ].join(' '),
  variants: {
    expanded: {
      true: 'bg-white/10 dark:bg-white/5',
      false: '',
    },
    disabled: {
      true: 'opacity-50 pointer-events-none',
      false: '',
    },
  },
  defaultVariants: {
    expanded: 'false',
    disabled: 'false',
  },
});

const accordionHeaderVariants = cva({
  base: [
    'relative w-full flex items-center justify-between p-4 text-left',
    'cursor-pointer transition-all duration-300 ease-out',
    'hover:bg-white/20 dark:hover:bg-white/10',
    'focus:ring-2 focus:ring-primary-500/50 focus:ring-offset-2 focus:outline-none',
    'transform-gpu hover:scale-[1.01] active:scale-[0.99]',
    'group',
  ].join(' '),
  variants: {
    variant: {
      default: '',
      elevated: 'hover:bg-primary-50/20 dark:hover:bg-primary-900/20',
      premium: 'hover:bg-linear-to-r hover:from-white/25 hover:to-primary-50/20',
      minimal: 'hover:bg-gray-50/15 dark:hover:bg-gray-800/15',
      bordered: 'hover:bg-white/15 dark:hover:bg-white/8',
    },
    size: {
      sm: 'p-3 text-sm',
      md: 'p-4 text-base',
      lg: 'p-6 text-lg',
      xl: 'p-8 text-xl',
    },
    expanded: {
      true: 'text-primary-700 dark:text-primary-300',
      false: 'text-gray-700 dark:text-gray-200',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    expanded: 'false',
  },
});

const accordionContentVariants = cva({
  base: ['relative overflow-hidden transition-all duration-300 ease-out', 'bg-white/5 dark:bg-white/3'].join(' '),
  variants: {
    size: {
      sm: 'px-3 text-sm',
      md: 'px-4 text-base',
      lg: 'px-6 text-lg',
      xl: 'px-8 text-xl',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const chevronVariants = cva({
  base: ['transition-transform duration-300 ease-out', 'text-gray-500 dark:text-gray-400'].join(' '),
  variants: {
    expanded: {
      true: 'transform rotate-180 text-primary-600 dark:text-primary-400',
      false: 'transform rotate-0',
    },
  },
  defaultVariants: {
    expanded: 'false',
  },
});

@Component({
  selector: 'ui-accordion-glass',
  standalone: true,
  imports: [IconGlassComponent],
  templateUrl: './accordion-glass.component.html',
})
export class AccordionGlassComponent {
  @Input() items: AccordionItem[] = [];
  @Input() variant: AccordionVariant = 'default';
  @Input() size: AccordionSize = 'md';
  @Input() allowMultiple: boolean = false;
  @Input() closeOthers: boolean = true;
  @Input() themeSection: ThemeSection = 'global';

  @Output() itemToggle = new EventEmitter<{ item: AccordionItem; expanded: boolean }>();
  @Output() itemClick = new EventEmitter<AccordionItem>();

  protected expandedItems = signal<Set<string>>(new Set());

  protected iconSize = computed(() => {
    const sizeMap = {
      sm: 'sm' as const,
      md: 'sm' as const,
      lg: 'md' as const,
      xl: 'lg' as const,
    };
    return sizeMap[this.size];
  });

  protected computedAccordionClasses = computed(() => {
    return accordionVariants({
      variant: this.variant,
    });
  });

  protected getItemClasses(item: AccordionItem): string {
    return accordionItemVariants({
      expanded: this.isExpanded(item.id) ? 'true' : 'false',
      disabled: item.disabled ? 'true' : 'false',
    });
  }

  protected getHeaderClasses(item: AccordionItem): string {
    return accordionHeaderVariants({
      variant: this.variant,
      size: this.size,
      expanded: this.isExpanded(item.id) ? 'true' : 'false',
    });
  }

  protected computedContentClasses = computed(() => {
    return accordionContentVariants({
      size: this.size,
    });
  });

  protected getChevronClasses(itemId: string): string {
    return chevronVariants({
      expanded: this.isExpanded(itemId) ? 'true' : 'false',
    });
  }

  protected isExpanded(itemId: string): boolean {
    return this.expandedItems().has(itemId);
  }

  protected hasExpandedItems(): boolean {
    return this.expandedItems().size > 0;
  }

  protected toggleItem(itemId: string): void {
    const item = this.items.find((i) => i.id === itemId);
    if (!item || item.disabled) return;

    const currentlyExpanded = this.isExpanded(itemId);
    const newExpandedItems = new Set(this.expandedItems());

    if (currentlyExpanded) {
      newExpandedItems.delete(itemId);
    } else {
      if (!this.allowMultiple && this.closeOthers) {
        newExpandedItems.clear();
      }
      newExpandedItems.add(itemId);
    }

    this.expandedItems.set(newExpandedItems);
    this.itemToggle.emit({ item, expanded: !currentlyExpanded });
    this.itemClick.emit(item);
  }

  protected handleKeyDown(event: KeyboardEvent, currentIndex: number): void {
    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = this.getNextEnabledItemIndex(currentIndex, 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        nextIndex = this.getNextEnabledItemIndex(currentIndex, -1);
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = this.getNextEnabledItemIndex(-1, 1);
        break;
      case 'End':
        event.preventDefault();
        nextIndex = this.getNextEnabledItemIndex(this.items.length, -1);
        break;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const currentItem = this.items[currentIndex];
        if (currentItem) {
          this.toggleItem(currentItem.id);
        }
        return;
      }
    }

    if (nextIndex !== currentIndex) {
      setTimeout(() => {
        const nextItem = this.items[nextIndex];
        if (!nextItem) return;
        const headerButton = document.getElementById(`accordion-header-${nextItem.id}`);
        headerButton?.focus();
      });
    }
  }

  protected getContentHeight(itemId: string): string {
    if (typeof document === 'undefined') return 'auto';

    const contentElement = document.getElementById(`content-${itemId}`);
    if (!contentElement) return 'auto';

    return `${contentElement.scrollHeight}px`;
  }

  protected hasCustomContent(itemId: string): boolean {
    if (typeof document === 'undefined') return false;

    const customContent = document.querySelector(`[data-accordion="${itemId}"]`);
    return !!customContent;
  }

  private getNextEnabledItemIndex(currentIndex: number, direction: number): number {
    let nextIndex = currentIndex + direction;

    while (nextIndex >= 0 && nextIndex < this.items.length) {
      const candidate = this.items[nextIndex];
      if (candidate && !candidate.disabled) {
        return nextIndex;
      }
      nextIndex += direction;
    }

    // Wrap around
    if (direction > 0) {
      for (let i = 0; i < currentIndex; i++) {
        const candidate = this.items[i];
        if (candidate && !candidate.disabled) {
          return i;
        }
      }
    } else {
      for (let i = this.items.length - 1; i > currentIndex; i--) {
        const candidate = this.items[i];
        if (candidate && !candidate.disabled) {
          return i;
        }
      }
    }

    return currentIndex;
  }

  // Public methods for programmatic control
  expandItem(itemId: string): void {
    const item = this.items.find((i) => i.id === itemId);
    if (!item || item.disabled || this.isExpanded(itemId)) return;

    const newExpandedItems = new Set(this.expandedItems());
    if (!this.allowMultiple && this.closeOthers) {
      newExpandedItems.clear();
    }
    newExpandedItems.add(itemId);
    this.expandedItems.set(newExpandedItems);
  }

  collapseItem(itemId: string): void {
    if (!this.isExpanded(itemId)) return;

    const newExpandedItems = new Set(this.expandedItems());
    newExpandedItems.delete(itemId);
    this.expandedItems.set(newExpandedItems);
  }

  expandAll(): void {
    if (!this.allowMultiple) return;

    const enabledIds = this.items.filter((item) => !item.disabled).map((item) => item.id);
    this.expandedItems.set(new Set(enabledIds));
  }

  collapseAll(): void {
    this.expandedItems.set(new Set());
  }
}

/**
 * Usage Examples:
 *
 * Basic product FAQ accordion:
 * <ui-accordion-glass
 *   [items]="faqItems"
 *   variant="premium"
 *   size="lg"
 *   [allowMultiple]="true"
 * >
 *   <div data-accordion="shipping">
 *     <h4>Shipping Information</h4>
 *     <p>We offer free shipping on orders over $50...</p>
 *   </div>
 *   <div data-accordion="returns">
 *     <h4>Return Policy</h4>
 *     <p>You can return items within 30 days...</p>
 *   </div>
 * </ui-accordion-glass>
 *
 * E-commerce product details:
 * <ui-accordion-glass
 *   [items]="productDetails"
 *   variant="elevated"
 *   [closeOthers]="false"
 *   (itemToggle)="onSectionToggle($event)"
 * >
 *   <div data-accordion="specs">
 *     <ui-table-glass [data]="productSpecs" variant="minimal"></ui-table-glass>
 *   </div>
 *   <div data-accordion="warranty">
 *     <ui-card-glass variant="subtle">
 *       <h4>2-Year Limited Warranty</h4>
 *       <p>Coverage details and terms...</p>
 *     </ui-card-glass>
 *   </div>
 * </ui-accordion-glass>
 *
 * Settings accordion:
 * <ui-accordion-glass
 *   [items]="settingsItems"
 *   variant="minimal"
 *   size="md"
 * >
 *   <div data-accordion="account">
 *     <ui-input label="Display Name" [value]="user.name"></ui-input>
 *     <ui-input label="Email" [value]="user.email"></ui-input>
 *   </div>
 *   <div data-accordion="privacy">
 *     <ui-toggle-glass label="Email notifications"></ui-toggle-glass>
 *     <ui-toggle-glass label="Marketing emails"></ui-toggle-glass>
 *   </div>
 * </ui-accordion-glass>
 *
 * Item configuration:
 * faqItems: AccordionItem[] = [
 *   {
 *     id: 'shipping',
 *     title: 'Shipping & Delivery',
 *     icon: 'truck',
 *     badge: 'New'
 *   },
 *   {
 *     id: 'returns',
 *     title: 'Returns & Exchanges',
 *     icon: 'rotate-ccw',
 *     content: 'Simple return policy content...'
 *   },
 *   {
 *     id: 'warranty',
 *     title: 'Warranty Information',
 *     icon: 'shield',
 *     disabled: true
 *   }
 * ];
 *
 * Programmatic control:
 * <ui-accordion-glass #accordion [items]="items"></ui-accordion-glass>
 * <ui-button-glass (click)="accordion.expandAll()">Expand All</button>
 * <ui-button-glass (click)="accordion.collapseAll()">Collapse All</button>
 *
 * Different variants:
 * <ui-accordion-glass variant="default" [items]="items"></ui-accordion-glass>
 * <ui-accordion-glass variant="elevated" [items]="items"></ui-accordion-glass>
 * <ui-accordion-glass variant="premium" [items]="items"></ui-accordion-glass>
 * <ui-accordion-glass variant="minimal" [items]="items"></ui-accordion-glass>
 * <ui-accordion-glass variant="bordered" [items]="items"></ui-accordion-glass>
 */
